import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { ConfirmCreateFieldDialog } from '../-components/ConfirmCreateFieldDialog';
import { CustomFieldDialog } from '../-components/CustomFieldDialog';
import { CustomFieldsTable } from '../-components/CustomFieldsTable';
import { DeleteCustomFieldsDialog } from '../-components/DeleteCustomFieldsDialog';
import { adminCustomFieldsQueryOptions } from '../-hooks/useAdminCustomFields';
import { useCustomInformationFieldsPage } from '../-hooks/useCustomInformationFieldsPage';

const customFieldsSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute(
  '/management/custom-information-fields/(pages)/',
)({
  validateSearch: customFieldsSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed on purpose: the loader is a prefetch, and letting it reject hands
  // the route to the router's error boundary, which hides the table's own
  // "Could not load" banner. Matches the other management lists.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminCustomFieldsQueryOptions(deps))
      .catch(() => undefined),
  component: CustomInformationFieldsPage,
});

function CustomInformationFieldsPage() {
  const page = useCustomInformationFieldsPage();

  return (
    <PageContainer>
      <CustomFieldsTable page={page} />
      <CustomFieldDialog
        state={page.dialog}
        field={page.field}
        isLoadingField={page.isLoadingField}
        isPending={page.isPending}
        onClose={page.closeDialog}
        onSubmit={page.submitForm}
        onDelete={() => {
          if (page.field) page.askDelete(page.field);
        }}
      />
      <ConfirmCreateFieldDialog
        open={page.pendingCreate !== null}
        isPending={page.isPending}
        onCancel={page.cancelCreate}
        onConfirm={() => void page.confirmCreate()}
      />
      <DeleteCustomFieldsDialog
        pending={page.pendingDelete}
        isPending={page.isPending}
        onCancel={page.cancelDelete}
        onConfirm={() => void page.confirmDelete()}
      />
    </PageContainer>
  );
}
